import _ from 'underscore';
import $ from 'jquery';
import 'bootstrap';
import Promise from 'bluebird';
import { DeterministicView, helpers } from '@bingads-webui/deterministic-view';
import { a11yPlugin } from '@bingads-webui/projection-grid-a11y';
import pgrid from 'projection-grid';
import { performanceLogPlugin } from '@bingads-webui-campaign/pgrid-performance-log';
import { placeholderPlugin } from '@bingads-webui/projection-grid-placeholder';
import { withUiBlock } from '@bingads-webui/uiblocker';
import { Constants } from '@bingads-webui-clientcenter/entity-utils';
import { keyCode } from '@bingads-webui/keycode';
import {
  changeEvents,
  entityHash,
  getEntityIds,
  navigateToEntity,
  coerceStatus,
  gridCardConfig,
  allMetrics as metrics,
  gridNameTemplate as nameTemplate,
  fetchEntityGridCombined,
  fetchGridData,
  processGridTileOptions,
  patchMetrics, patchMetric,
} from '@bingads-webui-campaign/overview-cards-common';
import coverTemplate from './cover-template.pug';
import placeholderTemplate from './placeholder-template.pug';
import template from './content-template.pug';
import performanceMetricHeaderTemplate from './performance-metric-header-template.pug';
import performanceMetricTemplate from './performance-metric-template.pug';

let formatMetric;
let formatMetricWithSign;
let formatChangePercentageWithSign;

function order(body) {
  return _.defaults({ body }, {
    PerformanceMetrics: {
      sup: 'PerformanceMetricsChangePercentage',
      sub: 'PerformanceMetricsChange',
    },
    PerformanceMetricsChange: {
      sup: 'PerformanceMetrics',
      sub: 'PerformanceMetricsChangePercentage',
    },
    PerformanceMetricsChangePercentage: {
      sup: 'PerformanceMetrics',
      sub: 'PerformanceMetricsChange',
    },
  }[body]);
}

function metricColumnProperty({
  PerformanceMetrics = {},
  PerformanceMetricsChange = {},
  PerformanceMetricsChangePercentage = {},
  Currency,
} = {}, metric) {
  return {
    PerformanceMetrics: PerformanceMetrics[metric],
    PerformanceMetricsChange: PerformanceMetricsChange[metric],
    PerformanceMetricsChangePercentage: PerformanceMetricsChangePercentage[metric],
    Currency,
  };
}

function formatCell(value, metric) {
  return {
    PerformanceMetrics: formatMetric(
      metric,
      value.PerformanceMetrics,
      { currency: value.Currency }
    ),
    PerformanceMetricsChange: formatMetricWithSign(
      metric,
      value.PerformanceMetricsChange,
      { currency: value.Currency }
    ),
    PerformanceMetricsChangePercentage:
      formatChangePercentageWithSign(value.PerformanceMetricsChangePercentage),
  };
}

/* @this GridCard */
function metricColumnTemplate({ value }, metric) {
  return performanceMetricTemplate({
    value: formatCell(value, metric),
    order: order(this.getState().body),
    showExtra: this.gridView.get('sortableHeader').name === metric && this.getState().dateRange.compared && this.typeName !== 'SearchTerm',
  });
}

const sortableMapping = {
  PerformanceMetrics: 'PerformanceMetrics',
  PerformanceMetricsChange: 'PoPPerformanceMetricsChange',
  PerformanceMetricsChangePercentage: 'PoPPerformanceMetricsChangePercentage',
};

const titleMapping = {
  Keyword: 'EntityType_Keyword_Plural',
  SearchTerm: 'Search_Terms',
  Campaign: 'EntityType_Campaign_Plural',
  Account: 'Dashboard_TopAccounts_Card_Title',
  AdGroup: 'EntityType_AdGroup_Plural',
};

const columnHeaderMapping = {
  Keyword: 'EntityType_Keyword_Singular',
  SearchTerm: 'ColumnName_SearchTerm',
  Campaign: 'EntityType_Campaign_Singular',
  Account: 'Entity_Account',
  AdGroup: 'EntityType_AdGroup_Singular',
};

/* @this GridCard */
function sortableAttribute(metric) {
  return `${sortableMapping[this.getState().body]}/${metric}`;
}

/* @this GridCard */
function metricColumn(metric) {
  return this.bingadsGridColumn({
    name: metric,
    title: this.s$i18n.property('PerformanceMetrics', metric),
    property: entity => metricColumnProperty(entity, metric),
    sortable: {
      key: sortableAttribute.call(this, metric),
      direction: -1,
    },
    colClasses: () =>
      (this.gridView.get('sortableHeader').name === metric ? ['performance-metric-col-sorting'] : ['performance-metric-col-non-sorting']),
    bodyClasses: () =>
      (this.gridView.get('sortableHeader').name === metric ? ['performance-metric-td-sorting'] : []),
    headClasses: () =>
      (this.gridView.get('sortableHeader').name === metric ? ['performance-metric-th-sorting'] : []),
    html: performanceMetricHeaderTemplate({
      metric,
      s$i18n: this.s$i18n,
      i18n: this.i18n,
    }),
    template: ({ value }) => metricColumnTemplate.call(this, { value }, metric),
  });
}

/* @this GridCard */
function nameColumn() {
  return this.bingadsGridColumn({
    name: 'Name',
    title: this.i18n.getString(columnHeaderMapping[this.typeName]),
    property: ({
      Id,
      Name,
      Text,
      Status,
      AdGroup,
      Campaign,
      MatchType,
      DeliveredMatchType,
      CampaignType,
      Keyword,
      Added,
      SearchTermStatus,
      ExperimentId,
      AccountInactiveReasons,
    }) => {
      let nameText = Text;

      if (this.typeName === 'Keyword') {
        nameText = Text;
        if (MatchType === 'Phrase') {
          nameText = `"${nameText}"`;
        } else if (MatchType === 'Exact') {
          nameText = `[${nameText}]`;
        }
      } else if (this.typeName === 'SearchTerm') {
        nameText = Text;
      } else {
        nameText = Name;
      }
      let isExperiment;

      if (this.typeName === 'Campaign') {
        isExperiment = !!ExperimentId;
      } else if (this.typeName !== 'Account') {
        isExperiment = !!Campaign.ExperimentId;
      }

      return {
        Name: nameText,
        keywordText: Text,
        Status: coerceStatus(Status, AccountInactiveReasons),
        hash: entityHash({
          id: Id,
          typeName: this.typeName,
          entitySet: this.entitySet,
          navigationLevel: this.navigationLevel,
          adGroup: AdGroup,
          campaign: Campaign,
          pageContext: this.pageContext,
          legacyRouter: this.legacyRouter,
          isSmartCampaign: CampaignType === 'Smart',
          status: Status,
        }),
        rawStatus: Status,
        typeName: this.typeName,
        ids: getEntityIds({
          id: Id,
          typeName: this.typeName,
          entitySet: this.entitySet,
          adGroup: AdGroup,
          campaign: Campaign,
        }),
        AdGroup: AdGroup || {},
        Campaign: Campaign || {},
        MatchType: MatchType || DeliveredMatchType,
        navigationLevel: this.navigationLevel,
        i18n: this.i18n,
        AccountActivityStatus: Constants.AccountActivityStatus,
        added: Added,
        campaignType: CampaignType,
        matchingKeyword: Keyword ? Keyword.Text : null,
        searchTermStatus: SearchTermStatus,
        isExperiment,
      };
    },
    template: nameTemplate,
  });
}

/* @this GridCard */
function metricColumns() {
  return _.object(_.map(metrics(this.config && this.config.get('Permissions'), this.accountsMode), metric => [metric, metricColumn.call(this, metric)]));
}

/* @this GridCard */
function removeDropdown() {
  if (this.dropdown) {
    this.dropdown.$el.closest('.grid-card-header').find('button').attr('aria-expanded', false);
    this.dropdown.remove();
    this.dropdown = null;
  }

  if (this.onClickDocument) {
    $(document).off('click', this.onClickDocument);
    this.onClickDocument = null;
  }
}

/* @this GridCardContent */
function updatePopupPosition(evt) {
  const $target = $(evt.currentTarget);
  const hoverContent = $target.find('.hover-content');

  if (hoverContent.attr('style')) {
    hoverContent.attr('style', '');
  }

  const $viewport = $target.closest('.viewport');
  const scrollTop = $viewport.scrollTop();

  if (scrollTop <= 0) {
    return;
  }

  if ($target.offset().top - $viewport.offset().top > 100) {
    hoverContent.css('bottom', `${scrollTop + 35}px`);
    hoverContent.css('top', 'auto');
  } else {
    hoverContent.css('top', `${5 - scrollTop}px`);
    hoverContent.css('bottom', 'auto');
  }
}


function showPopup(evt) {
  updatePopupPosition(evt);
  const $target = $(evt.currentTarget);
  const $popup = $target.find('.grid-cell--Name__hover-container');
  $popup.css({ display: 'block' });
}

function hidePopup(evt) {
  const $target = $(evt.currentTarget);
  const $popup = $target.find('.grid-cell--Name__hover-container');
  $popup.css({ display: 'none' });
}


/* @this GridCardContent */
function getAllColumns() {
  const allMetricColumns = metricColumns.call(this);

  return [nameColumn.call(this), ..._.map(
    this.getState().columns,
    metric => allMetricColumns[metric]
  )];
}

/* @this GridCardContent */
function refetch() {
  this.gridView.refresh(true);
}

/* @this GridCardContent */
function updateGridBody() {
  const sortableHeader = this.gridView.get('sortableHeader') || {};
  const {
    orderby,
    direction,
    directionIndex,
  } = this.getState();
  const newSortableHeader = sortableHeader.name ? sortableHeader : {
    name: orderby,
    directionIndex,
  };

  this.gridView.patch({
    columns: getAllColumns.call(this),
    query: {
      orderby: [{
        key: sortableAttribute.call(this, newSortableHeader.name),
        direction,
      }],
    },
    sortableHeader: newSortableHeader,
  });

  refetch.call(this);
}

/* @this GridCardContent */
function updateGridColumn() {
  const {
    columns,
    orderby,
    direction,
    directionIndex,
  } = this.getState();

  if (!_.contains(columns, orderby)) {
    helpers.updateView(this, {
      orderby: _.first(columns),
    });
  }

  const sortableHeader = this.gridView.get('sortableHeader') || {};
  const newSortableHeader = _.contains(columns, sortableHeader.name) ? sortableHeader : {
    name: this.getState().orderby,
    directionIndex,
  };

  this.gridView.patch({
    columns: getAllColumns.call(this),
    query: {
      orderby: [{
        key: sortableAttribute.call(this, newSortableHeader.name),
        direction,
      }],
    },
    sortableHeader: newSortableHeader,
  });
  refetch.call(this);
  this.trigger('gridOptionChange');
}

/* @this GridCardContent */
function onGridSortableHeaderChange(changes) {
  const newOderBy = _.result(changes.query, 'orderby') || _.result(this.gridView.get('query'), 'orderby');
  const newDirection = newOderBy[0].direction;
  const newOderByColumn = changes.sortableHeader.name;
  const newDirectionIndex = changes.sortableHeader.directionIndex;
  const { orderby, direction, directionIndex } = this.getState();

  if (orderby !== newOderByColumn ||
    direction !== newDirection ||
    directionIndex !== newDirectionIndex) {
    this.setState({
      orderby: newOderByColumn,
      direction: newDirection,
      directionIndex: newDirectionIndex,
      fetchSingleTile: true,
    });
    this.instrumentation.logMessage(
      [this.constants.LOGCATEGORY.TRACE],
      this.constants.LOGLEVEL.INFO,
      'grid-card',
      `${this.navigationLevel} level ${this.typeName} type new sort order picked.`
    );
    this.trigger('gridOptionChange');
  }
}

class CombinedCallEntity {
  constructor({
    entitySet,
    getParentState,
    dateUtil,
    jsdata,
    getCardsConfig,
    permissions,
    accountsMode,
  }) {
    this.entitySet = entitySet;
    this.getParentState = getParentState;
    this.dateUtil = dateUtil;
    this.jsdata = jsdata;
    this.getCardsConfig = getCardsConfig;
    this.permissions = permissions;
    this.accountsMode = accountsMode;
  }

  findAll({ orderBy }) {
    const { dateRange, fetchSingleTile, filter } = this.getParentState();
    return fetchEntityGridCombined({
      entitySet: this.entitySet,
      dateRange,
      comparisonDateRange: dateRange.compared,
      orderBy,
      jsdata: this.jsdata,
      dateUtil: this.dateUtil,
      getCardsConfig: this.getCardsConfig,
      fetchSingleTile,
      filter,
      permissions: this.permissions,
      accountsMode: this.accountsMode,
    });
  }
}

class GridDataEntity {
  constructor({
    entitySet,
    getParentState,
    dateUtil,
    jsdata,
    permissions,
    typeId,
  }) {
    this.entitySet = entitySet;
    this.getParentState = getParentState;
    this.dateUtil = dateUtil;
    this.jsdata = jsdata;
    this.permissions = permissions;
    this.typeId = typeId;
  }

  findAll() {
    const {
      dateRange,
      orderby,
      direction,
      selectedMenuItem,
      filter,
      columns,
    } = this.getParentState();
    return fetchGridData({
      entitySet: this.entitySet,
      dateRange,
      options: {
        orderby,
        direction,
        selectedMenuItem,
        columns,
      },
      filter,
      jsdata: this.jsdata,
      dateUtil: this.dateUtil,
      permissions: this.permissions,
      dfmCacheKey: this.typeId,
    });
  }
}

class DummyDataSource {
  findAll() {
    const data = [];
    data.totalCount = 0;
    return Promise.resolve(data);
  }
}

export default class GridCardContent extends DeterministicView {
  constructor(options) {
    super(_.extend({}, options, { renderInSync: true }));
  }

  initialize({
    entitySet,
    navigationLevel,
    parent,
    selectedMenuItem,
    columns,
    orderby,
    direction,
    directionIndex,
    dateRange,
    filter,
    getCardsConfig,
    fetchSingleTile = false,
    preexecuteFlag = false,
    accountsMode,
    deps,
  }) {
    this.parent = parent;
    this.entitySet = entitySet;
    this.typeName = entitySet.type.elementType.shortName;
    this.navigationLevel = navigationLevel;
    this.accountsMode = accountsMode;
    this.config = deps.config;
    this.i18n = deps.i18n;
    this.s$i18n = deps.s$i18n;
    this.pageContext = deps.pageContext;
    this.legacyRouter = deps.legacyRouter;
    this.createEditableFilterStorage = deps.createEditableFilterStorage;
    this.instrumentation = deps.instrumentation;
    this.constants = deps.constants;
    this.bingadsGridColumn = deps.bingadsGridColumn;
    this.metricFormatter = deps.metricFormatter;
    // eslint-disable-next-line prefer-destructuring
    formatMetric = deps.metricFormatter.formatMetric;
    // eslint-disable-next-line prefer-destructuring
    formatMetricWithSign = deps.metricFormatter.formatMetricWithSign;
    // eslint-disable-next-line prefer-destructuring
    formatChangePercentageWithSign = deps.metricFormatter.formatChangePercentageWithSign;

    const permissions = this.config && this.config.get('Permissions');
    const tileOptions = _.defaults({
      body: selectedMenuItem,
      columns,
      orderby,
      direction,
      directionIndex,
      dateRange,
      filter,
      fetchSingleTile,
    }, gridCardConfig.defaultOptions);

    tileOptions.columns = tileOptions.columns &&
      patchMetrics({ permissions, metrics: tileOptions.columns, accountsMode });
    tileOptions.orderby = tileOptions.orderby &&
      patchMetric({ permissions, metric: tileOptions.orderby, accountsMode });

    const initialState = processGridTileOptions({
      options: tileOptions,
      permissions,
      accountsMode,
    });

    this.setState(initialState);
    if (!_.isEqual(tileOptions, initialState)) {
      _.defer(() => this.trigger('gridOptionChange'));
    }

    const gridFactory = pgrid.factory({ vnext: true });

    gridFactory
      .use(a11yPlugin)
      .use(placeholderPlugin)
      .use(performanceLogPlugin);

    const {
      gridView,
    } = gridFactory
      .create({
        name: `OverviewTop${this.typeName}`,
        tableClasses: ['table'],
        tableAttributes: { 'aria-label': this.i18n.getString(titleMapping[this.typeName]) },
        dataSource: {
          type: 'jsdata',
          // eslint-disable-next-line no-nested-ternary
          entity: preexecuteFlag ? new DummyDataSource() : ((this.typeName === 'Account' ||
            (this.typeName === 'Campaign')) ?
            new CombinedCallEntity({
              entitySet,
              getParentState: this.getState.bind(this),
              dateUtil: deps.dateUtil,
              jsdata: deps.jsdata,
              getCardsConfig,
              permissions,
              accountsMode: this.accountsMode,
            }) : new GridDataEntity({
              entitySet,
              getParentState: this.getState.bind(this),
              dateUtil: deps.dateUtil,
              jsdata: deps.jsdata,
              permissions,
              typeId: this.options.typeId,
            })),
        },
        columns: getAllColumns.call(this),
        plugins: {
          a11y: { i18n: this.i18n },
          performanceLog: {
            appConfig: deps.config,
            logger: deps.instrumentation,
          },
          placeholder: {
            viewRoot: this.parent.el,
            container: '.no-data',
            template: placeholderTemplate,
            templateOptions: {
              i18n: this.i18n,
            },
          },
        },
        events: {
          'click .metric-picker__button--caret': (e) => {
            e.stopPropagation();
            e.preventDefault();
            // Event propagation is stopped above.
            // Click on header to bubble up event to window without side effect.
            this.$('.header').click();
            const $metricPicker = this.$(e.currentTarget).closest('.metric-picker');
            const currentMetric = $metricPicker[0].dataset.metric;
            const isDropdownOpenfortheColumn = this.dropdown && $metricPicker.closest('.grid-card-header')[0].contains(this.dropdown.el);

            removeDropdown.call(this);

            if (!isDropdownOpenfortheColumn) {
              const currentColumns = this.gridView.get('columns');

              import(/* webpackMode: 'eager' */ '@bingads-webui-campaign/overview-metric-picker').then(({ MetricPicker }) => {
                import(/* webpackMode: 'eager' */ './metric-picker-template.pug').then(({ default: metricPickerTemplate }) => {
                  import(/* webpackMode: 'eager' */ './dropdown-template.pug').then(({ default: dropdownMenuTemplate }) => {
                    this.dropdown = new MetricPicker({
                      buttonTemplate: () => null,
                      template: metricPickerTemplate,
                      dropdownMenuTemplate,
                      metricFormatter: this.metricFormatter,
                      s$i18n: this.s$i18n,
                      i18n: this.i18n,
                      accountsMode: this.accountsMode,
                      deps,
                    }).setState({
                      current: currentMetric,
                      metrics: _.difference(
                        metrics(permissions, this.accountsMode),
                        _.map(currentColumns, col => col.name)
                      ),
                    });

                    this.dropdown.on('pick', ({ metric }) => {
                      const stateColumns = _.clone(this.getState().columns);
                      const index = _.findIndex(stateColumns, col => col === currentMetric);

                      this.instrumentation.logMessage(
                        [this.constants.LOGCATEGORY.TRACE],
                        this.constants.LOGLEVEL.INFO,
                        'grid-card',
                        `${this.navigationLevel} level ${this.typeName} type new metric picked.`
                      );
                      stateColumns[index] = metric;
                      helpers.updateView(this, {
                        columns: stateColumns,
                      });
                    });

                    $metricPicker.closest('.grid-card-header').append(this.dropdown.el);
                    $metricPicker.find('button').attr('aria-expanded', true);

                    this.onClickDocument = (event) => {
                      if (this.dropdown && !this.dropdown.el.contains(event.target)) {
                        removeDropdown.call(this);
                      }
                    };
                    $(document).on('click', this.onClickDocument);
                  });
                });
              });
            }
          },
        },
        scrolling: {
          virtualized: false,
          header: {
            type: 'fixed',
            attributes: { 'aria-label': this.i18n.getString('{{tableName}} table header', { tableName: this.i18n.getString(titleMapping[this.typeName]) }) },
          },
        },
      });

    this.gridView = gridView;
    updateGridColumn.call(this);

    this.listenTo(this.model, changeEvents('dateRange', 'filter'), () => {
      this.setState({ fetchSingleTile: false });
      refetch.call(this);
    });

    this.listenTo(this.model, changeEvents('body'), () => {
      this.setState({ fetchSingleTile: true });
      updateGridBody.call(this);
    });

    this.listenTo(this.model, changeEvents('columns'), () => {
      this.setState({ fetchSingleTile: true });
      updateGridColumn.call(this);
    });

    this.gridView.on('willUpdate', (changes) => {
      if (_.has(changes, 'sortableHeader')) {
        onGridSortableHeaderChange.call(this, changes);
      }
    });

    this.gridView.on('didUpdate', () => {
      this.setupScrollBarCover();
    });

    this.gridView.on('didRedraw', () => {
      this.$('.viewport').scrollTop(0);
      this.setupScrollBarCover();
      const headerOption = this.$('.column-header.clickable');
      const firstRow = this.$('.viewport tr:not(.top-filler):first a');
      if (firstRow.length > 0) {
        firstRow.focus();
      } else if (headerOption.length > 0) {
        headerOption.get(0).focus();
      }
    });

    this.gridView.once('didReload', () => {
      this.parent.willRenderWithData();
      this.dataFetched = true;
      this.gridView.once('didRedraw', () => {
        this.parent.renderedWithData();
      });
    });

    this.gridView.once('willReload', () => {
      this.parent.willFetchData();
    });

    this.gridView.on('willReload', () => {
      if (!this.unblock && !this.parent.unblock) {
        this.unblock = withUiBlock(this.$el.parent(), this.i18n.getString('Loading'));
      }
    });

    this.gridView.on('didReload', () => {
      if (this.unblock) {
        this.unblock();
        this.unblock = null;
      }
      this.parent.unblockCard();
    });
  }

  get className() {
    return 'grid-content';
  }

  get events() {
    return {
      'click .grid-cell--Name a': event => navigateToEntity({
        event,
        pageContext: this.pageContext,
        legacyRouter: this.legacyRouter,
        createEditableFilterStorage: this.createEditableFilterStorage,
        cardType: 'grid-card',
        instrumentation: this.instrumentation,
        constants: this.constants,
      }),
      'mouseenter .grid-cell.grid-cell--Name': showPopup,
      'mouseleave .grid-cell.grid-cell--Name': hidePopup,
      'focus .grid-cell.grid-cell--Name': showPopup,
      'blur .grid-cell.grid-cell--Name': hidePopup,
      'keyup .grid-cell.grid-cell--Name': (event) => {
        if (event.keyCode === keyCode.Escape) {
          hidePopup.call(this, event);
        }
      },
    };
  }

  setupScrollBarCover() {
    if (!this.dataFetched) {
      return;
    }
    // If have a scroll bar, cover it per design.
    // Scroll bar will only be shown on hover.
    const viewport = this.gridView.$('.viewport');
    const viewportWidth = viewport.width();
    const table = $('table', viewport);
    const tableWidth = table.width();

    if (viewportWidth && tableWidth && (viewportWidth > tableWidth)) {
      // we have a scroll bar.
      const scrollBarWidth = viewportWidth - tableWidth;
      const scrollBarHeight = viewport.height();

      if (this.$('.scroll-bar-cover').length === 0) {
        // If we don't have a cover, create one.
        viewport.append(coverTemplate());
      }

      $('.scroll-bar-cover', this.$el.closest('.grid-card')).css({
        width: scrollBarWidth,
        height: scrollBarHeight,
      });

      // expand the viewport area to accommodate the scroll bar.
      const expand = Math.min(scrollBarWidth, 16);

      viewport.css({ marginRight: -expand });
    } else if (this.$('.scroll-bar-cover').length > 0) {
      // remove the scroll bar related change
      this.$('.scroll-bar-cover').remove();
      viewport.css({ marginRight: 0 });
    }
  }

  get template() {
    return template;
  }

  renderTrunk() {
    super.renderTrunk();
    this.gridView.render();
    this.$el.prepend(this.gridView.el);
    return this;
  }

  remove() {
    removeDropdown.call(this);
    this.gridView.remove();

    if (this.unblock) {
      this.unblock();
    }
    super.remove();
  }
}
